import {z} from 'zod';
import {
  targetTupleSchema,
  targetObjectSchema,
  targetTransforms,
  TargetObject,
} from '../target';
import {timingConfigSchema} from './timing';
import {animationPropertiesSchema} from './animation';
import {pluginConfigSchema} from '../shared';

export const timelineActionIdSchema = z.string();
export type TimelineActionId = z.infer<typeof timelineActionIdSchema>;

export const splitTextMaskSchema = z
  .enum(['chars', 'words', 'lines'])
  .optional();
export type SplitTextMask = z.infer<typeof splitTextMaskSchema>;

export const splitTextTypeSchema = z.enum(['chars', 'words', 'lines']);
export type SplitTextType = z.infer<typeof splitTextTypeSchema>;

export const timelineActionSplitTextSchema = z.union([
  z.object({
    type: splitTextTypeSchema,
    mask: splitTextMaskSchema,
  }),
  // Backwards compatibility below
  z.literal('none'),
  z.literal('chars'),
  z.literal('words'),
  z.literal('lines'),
]);
export type TimelineActionSplitText = z.infer<
  typeof timelineActionSplitTextSchema
>;

export const tweenTypeSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);

const baseTimelineAction = {
  /** Unique identifier for this action */
  id: timelineActionIdSchema,
  /** Human-friendly name for display/organization */
  name: z.string(),
  /** Optional reference to an action preset */
  presetId: z.string().optional(),
  /** Color for the action (visualized in the UI) */
  color: z.string().optional(),
  /** Whether the action is hidden (excluded from preview and publish) */
  hidden: z.boolean().optional(),
  /**
   * Timing and easing configuration. Action timing has no Speed slider;
   * `0` was opened only for timeline `settings.speed` (DES-7569), so this
   * keeps the historical `.positive()` bound.
   */
  timing: timingConfigSchema.extend({
    speed: z.number().positive().optional(),
  }),
  /** Tween type (to, from, or fromTo). Defaults to "to" (0) */
  tt: tweenTypeSchema.optional(),
  /** Properties to animate, organized by extension */
  properties: animationPropertiesSchema,
  /** Optional text splitting configuration for text animations. Defaults to "none" */
  splitText: timelineActionSplitTextSchema.optional(),
  // Plugin-owned JSON-safe config; plugins narrow it at their boundary.
  pluginConfig: pluginConfigSchema.optional(),
};

export const timelineActionClientSchema = z.object({
  ...baseTimelineAction,
  /** Elements to target with this animation */
  targets: z.array(targetTupleSchema),
});
export type TimelineActionClient = z.infer<typeof timelineActionClientSchema>;
export type TimelineAction = TimelineActionClient;

/** Plugin-specific action config. Plugins narrow this at their boundary. */
export type ActionPluginConfig = NonNullable<
  TimelineActionClient['pluginConfig']
>;

export const timelineActionServerSchema = z.object({
  ...baseTimelineAction,
  /** Elements to target with this animation */
  targets: z.array(targetObjectSchema),
});
export type TimelineActionServer = z.infer<typeof timelineActionServerSchema>;

export const timelineActionTransforms = {
  clientToServer: (client: TimelineActionClient): TimelineActionServer => {
    return {
      ...client,
      targets: client.targets?.map((t) => targetTransforms.tupleToObject(t)),
    };
  },

  serverToClient: (server: TimelineActionServer): TimelineActionClient => {
    return {
      ...server,
      targets: server.targets?.map((t) =>
        targetTransforms.objectToTuple(t as TargetObject)
      ),
    };
  },
};
